// Joining a family from the record's Family tab (UC-PATIENTS-027; PAT-41, PAT-42, PAT-45; BR-05b): a main account with no
// dependants of its own — a child approved from the kiosk as an account of their own — becomes a dependant of another main
// member, with that family's next suffix; every record stays with the patient and the old code stays a searchable alias. The
// patient's own contact details and medical aid are left as they are. No Control PIN (Q-11, as detachment); one key per body.
// The inverse of the detach dialog (PatientsDetach.tsx), in its vocabulary.
import { JOIN_FAMILY_REASONS, RELATIONSHIPS_TO_MAIN, type JoinFamilyInput, type JoinFamilyResult, type PatientView, type RelationshipToMain } from "@visionx/contracts";
import { useMemo, useState } from "react";
import { newIdempotencyKey } from "../api/client";
import { patients } from "../api/endpoints.patients";
import { describeError, isApiError } from "../api/problem";
import { Alert, Field, Modal, Select, TextInput } from "../ui/bits";
import { PatientPicker, RELATIONSHIP_LABEL, patientName, type PickedPatient } from "./PatientsShared";

const JOIN_REASON_LABEL: Record<JoinFamilyInput["reason"], string> = { DataCorrection: "Registered on their own by mistake", Other: "Other" };
const REFUSAL: Record<string, string> = {
  has_dependants: "This patient has dependants of their own. Move them to another main member first.",
  not_a_main_account: "This patient is already a dependant; move them with Detach… on their main member's Family tab.",
  not_active: "This patient is Prohibited or Archived; reactivate them first.",
};

export function JoinFamilyModal({ p, onClose, onDone }: { p: PatientView; onClose: () => void; onDone: (r: JoinFamilyResult, target: PickedPatient, relationshipToMain: RelationshipToMain) => void }) {
  const [target, setTarget] = useState<PickedPatient | null>(null);
  const [relationship, setRelationship] = useState<"" | RelationshipToMain>("");
  const [reason, setReason] = useState<JoinFamilyInput["reason"]>("DataCorrection");
  const [reasonText, setReasonText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const text = reasonText.trim();
  // One key per logical request: another family, relationship or reason is another body (BR-TENANCY-013).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const key = useMemo(() => newIdempotencyKey(), [target?.id, relationship, reason, text]);
  const fieldErr = (path: string) => (isApiError(error) ? error.field(path)?.message : undefined);
  const ready = !!target && !!relationship && (reason !== "Other" || !!text);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body: JoinFamilyInput = { targetMainAccountId: target!.id as never, relationshipToMain: relationship as RelationshipToMain, reason, ...(text ? { reasonText: text } : {}) };
      const { data } = await patients.joinFamily(p.id, body, key);
      onDone(data, target!, relationship as RelationshipToMain);
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  const refusal = isApiError(error) && typeof error.meta.reason === "string" ? REFUSAL[error.meta.reason] : undefined;
  const shown = error !== null && !fieldErr("targetMainAccountId") && !fieldErr("reasonText") && !fieldErr("relationshipToMain");
  return (
    <Modal title={`Join ${patientName(p)} to another family`} meta={<><span className="mono">{p.accountCode}</span> · every document, consent and appointment stays with them</>} wide onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy || !ready}>{busy ? "Joining…" : "Join family"}</button>
    </>}>
      {shown && <Alert tone="crit"><b>Not joined.</b> {refusal ?? describeError(error)}</Alert>}
      <PatientPicker label="New main member" required value={target} onPick={(t) => setTarget(t)} mainAccountsOnly exclude={[p.id]} error={fieldErr("targetMainAccountId")} help="A main member who is Active or Dormant; the patient takes the family's next suffix" />
      <div className="grid-2">
        <Field label="Relationship to the new main member" required htmlFor="join-rel" error={fieldErr("relationshipToMain")}>
          <Select id="join-rel" value={relationship} onChange={(e) => setRelationship(e.target.value as RelationshipToMain | "")}>
            <option value="">Choose…</option>
            {RELATIONSHIPS_TO_MAIN.map((r) => <option key={r} value={r}>{RELATIONSHIP_LABEL[r]}</option>)}
          </Select>
        </Field>
        <Field label="Reason" required htmlFor="join-reason">
          <Select id="join-reason" value={reason} onChange={(e) => setReason(e.target.value as JoinFamilyInput["reason"])}>{JOIN_FAMILY_REASONS.map((r) => <option key={r} value={r}>{JOIN_REASON_LABEL[r]}</option>)}</Select>
        </Field>
      </div>
      <Field label={reason === "Other" ? "Say what the other reason is" : "Note"} required={reason === "Other"} htmlFor="join-text" error={fieldErr("reasonText")}><TextInput id="join-text" value={reasonText} maxLength={500} onChange={(e) => setReasonText(e.target.value)} /></Field>
      <div className="help"><span className="mono">{p.accountCode}</span> stays findable as an alias and is never issued again. Their own mobile, email, address and medical aid stay as they are — to read the main member's details or be on their scheme, change that on the record afterwards.</div>
    </Modal>
  );
}
